import React, { useEffect, useMemo, useState } from 'react';
import { Search, LogOut, Download, FileSpreadsheet, FileText, ChevronLeft, ChevronRight } from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { createClient } from '@supabase/supabase-js';

const PASSWORD = '12345678910La.';
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export default function Admin() {
  // Auth and diagnostics
  const [authed, setAuthed] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [lastErrorStatus, setLastErrorStatus] = useState(null);
  const [lastFetchUrl, setLastFetchUrl] = useState('');
  const [sessionEmail, setSessionEmail] = useState(null);
  const [inserting, setInserting] = useState(false);
  const [notice, setNotice] = useState('');

  // Data
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);

  // Supabase env
  const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
  const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
  const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL;
  const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD;

  const supabase = useMemo(() => {
    if (!SUPABASE_URL || !SUPABASE_ANON_KEY) return null;
    try {
      return createClient(SUPABASE_URL, SUPABASE_ANON_KEY);
    } catch (_) {
      return null;
    }
  }, [SUPABASE_URL, SUPABASE_ANON_KEY]);

  useEffect(() => {
    const token = sessionStorage.getItem('admin_token');
    if (token === btoa(PASSWORD)) setAuthed(true);
  }, []);

  useEffect(() => {
    if (!authed) return;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, query, authed]);

  useEffect(() => {
    if (!authed) { setSessionEmail(null); return; }
    (async () => {
      try {
        const { data } = await supabase?.auth.getUser();
        setSessionEmail(data?.user?.email || null);
      } catch (_) {
        setSessionEmail(null);
      }
    })();
  }, [authed, supabase]);

  async function handleLogin(e) {
    e.preventDefault();
    setError('');
    if (password.trim() !== PASSWORD) {
      setError('Incorrect admin password');
      return;
    }
    // Try to authenticate silently using env credentials (to satisfy RLS)
    if (supabase && ADMIN_EMAIL && ADMIN_PASSWORD) {
      const { error: authError } = await supabase.auth.signInWithPassword({
        email: ADMIN_EMAIL,
        password: ADMIN_PASSWORD,
      });
      if (authError) {
        setError(`Login failed: ${authError.message}`);
        return;
      }
    }
    sessionStorage.setItem('admin_token', btoa(PASSWORD));
    setAuthed(true);
  }

  async function handleLogout() {
    try {
      await supabase?.auth.signOut();
    } catch (_) {}
    sessionStorage.removeItem('admin_token');
    setAuthed(false);
    setPassword('');
  }

  async function fetchData() {
    setLoading(true);
    setError('');
    setLastErrorStatus(null);
    setLastFetchUrl('');
    try {
      if (!supabase) throw new Error('Supabase not configured');
      const start = (page - 1) * pageSize;
      const end = start + pageSize - 1;
      const s = query.trim();
      let q = supabase
        .from('waitlist')
        .select('id,name,email,created_at', { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(start, end);
      if (s) q = q.or(`name.ilike.%${s}%,email.ilike.%${s}%`);
      const { data, count, error: selError } = await q;
      if (selError) throw selError;
      setItems(Array.isArray(data) ? data : []);
      setTotal(typeof count === 'number' ? count : (Array.isArray(data) ? data.length : 0));
    } catch (err) {
      setError(err?.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
  }

  function loadSampleData() {
    const sample = [
      { id: 1, name: 'Ada Lovelace', email: 'ada@example.com', created_at: new Date().toISOString() },
      { id: 2, name: 'Alan Turing', email: 'alan@example.com', created_at: new Date(Date.now() - 86400000).toISOString() },
    ];
    setItems(sample);
    setTotal(sample.length);
    setError('');
    setLastErrorStatus(null);
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  function exportCSV() {
    const headers = ['id', 'name', 'email', 'created_at'];
    const rows = items.map((r) => [r.id, r.name, r.email, r.created_at]);
    const csv = [headers.join(','), ...rows.map((row) => row.map((cell) => `"${String(cell || '').replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kreativ-waitlist-${new Date().toISOString()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function exportExcel() {
    const ws = XLSX.utils.json_to_sheet(items);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Waitlist');
    XLSX.writeFile(wb, `kreativ-waitlist-${new Date().toISOString().slice(0, 10)}.xlsx`);
  }

  function exportPDF() {
    const doc = new jsPDF();
    doc.text('kreatives Waitlist', 14, 16);
    const columns = [
      { header: 'Name', dataKey: 'name' },
      { header: 'Email', dataKey: 'email' },
      { header: 'Created', dataKey: 'created_at' },
    ];
    const rows = items.map(({ name, email, created_at }) => ({ name, email, created_at }));
    // @ts-ignore
    doc.autoTable({ columns, body: rows, startY: 22 });
    doc.save(`kreativ-waitlist-${new Date().toISOString().slice(0, 10)}.pdf`);
  }

  async function insertSampleSupabase() {
    setError('');
    setNotice('');
    if (!supabase) { setError('Supabase not configured'); return; }
    try {
      setInserting(true);
      const sampleName = `Test User ${new Date().toISOString().slice(11,19)}`;
      const sampleEmail = `test+${Date.now()}@example.com`;
      const { error: insError } = await supabase
        .from('waitlist')
        .insert({ name: sampleName, email: sampleEmail });
      if (insError) throw insError;
      setNotice(`Inserted sample: ${sampleEmail}`);
      await fetchData();
    } catch (err) {
      setError(err?.message || 'Insert failed');
    } finally {
      setInserting(false);
    }
  }

  if (!authed) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
          <div className="flex items-center gap-3 mb-6">
            <img src="/kreativ-logo.png" alt="kreatives" className="h-14 w-auto" />
            <div>
              <h1 className="text-xl font-semibold">kreatives Admin</h1>
              <p className="text-xs text-gray-500">Protected access</p>
            </div>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:border-indigo-600"
                placeholder="Enter admin password"
              />
            </div>
            {error && <div className="text-red-600 text-sm">{error}</div>}
            <button type="submit" className="w-full py-3 rounded-xl bg-indigo-600 text-white font-semibold hover:bg-indigo-700">Login</button>
          </form>
          <p className="text-xs text-gray-500 mt-4">Authorized use only.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <img src="/kreativ-logo.png" alt="kreatives" className="h-12 w-auto" />
            <h2 className="text-lg font-semibold">kreatives Admin</h2>
            <span className={`ml-2 text-xs px-2 py-1 rounded-full border ${sessionEmail ? 'bg-green-100 text-green-800 border-green-300' : 'bg-yellow-100 text-yellow-800 border-yellow-300'}`}>
              {sessionEmail ? `Auth: ${sessionEmail}` : 'Auth: anon'}
            </span>
          </div>
          <button className="flex items-center gap-2 text-sm px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-100" onClick={handleLogout}>
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search name or email"
                className="w-full pl-10 pr-3 py-3 border-2 border-indigo-200 rounded-xl focus:border-indigo-600 focus:ring-indigo-600"
              />
            </div>
            <button onClick={exportCSV} className="flex items-center gap-2 px-3 py-2 rounded-md bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100">
              <Download className="w-4 h-4" /> CSV
            </button>
            <button onClick={exportExcel} className="flex items-center gap-2 px-3 py-2 rounded-md bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-100">
              <FileSpreadsheet className="w-4 h-4" /> Excel
            </button>
            <button onClick={exportPDF} className="flex items-center gap-2 px-3 py-2 rounded-md bg-pink-50 text-pink-700 border border-pink-200 hover:bg-pink-100">
              <FileText className="w-4 h-4" /> PDF
            </button>

          </div>
        </div>

        {error && (
          <div className="mb-3 text-sm text-red-600">{error}{lastErrorStatus ? ` (status ${lastErrorStatus})` : ''}</div>
        )}
        {lastFetchUrl && (
          <div className="mb-3 text-xs text-gray-500 break-words">Request: {lastFetchUrl}</div>
        )}
        {notice && (
          <div className="mb-3 text-sm text-green-700">{notice}</div>
        )}

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-indigo-50">
              <tr>
                <th className="text-left px-4 py-2">Name</th>
                <th className="text-left px-4 py-2">Email</th>
                <th className="text-left px-4 py-2">Created</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td className="px-4 py-6" colSpan={3}>Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td className="px-4 py-6" colSpan={3}>No entries found</td></tr>
              ) : (
                items.map((row) => (
                  <tr key={row.id} className="border-t">
                    <td className="px-4 py-2">{row.name}</td>
                    <td className="px-4 py-2 text-gray-700">{row.email}</td>
                    <td className="px-4 py-2 text-gray-500">{new Date(row.created_at).toLocaleString()}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center gap-3">
            <div className="text-sm text-gray-600">Total: {total}</div>
            <span className="text-sm text-gray-600">Per page</span>
            <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} className="px-3 py-2 border-2 border-indigo-200 rounded-md focus:border-indigo-600">
              {PAGE_SIZE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <button
              className="px-2 py-1 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm text-indigo-700 font-medium">Page {page} of {Math.max(1, Math.ceil(total / pageSize))}</span>
            <button
              className="px-2 py-1 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= Math.max(1, Math.ceil(total / pageSize))}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}